import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Plus, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// A searchable dropdown over the values a project's FRS already uses, with a
// first-class way to type a value that does not exist yet. Room information
// only — never products, scopes or quantities.
export const RoomFieldCombo = ({ label, field, value, onChange, options = [], preferred = [],
                                testid, placeholder = "", allowNew = true, typeFirst = false,
                                required = false, optional = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(typeFirst);
  // Callers may own the testid namespace (the Add Room/Scope wizard does).
  const tid = testid || `frs-editor-new-room-${field}`;
  const box = useRef(null);

  useEffect(() => {
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);

  // The parent clears the field after a room is added — go back to the
  // searchable button so the next room starts from the list.
  useEffect(() => { if (!value.trim()) setAdding(typeFirst); }, [value, typeFirst]);

  // Escape belongs to the popover here, not to the dialog behind it. Radix
  // listens on document in the capture phase, so we have to get in first.
  useEffect(() => {
    if (!open && !adding) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      setAdding(false);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, adding]);

  const pref = useMemo(() => new Set(preferred.map((o) => o.toLowerCase())), [preferred]);
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const hit = options.filter((o) => !q || o.toLowerCase().includes(q));
    return [
      ["In this building", hit.filter((o) => pref.has(o.toLowerCase()))],
      [pref.size ? "Elsewhere in the job" : "Used in this job",
        hit.filter((o) => !pref.has(o.toLowerCase()))],
    ].filter(([, list]) => list.length > 0);
  }, [options, pref, query]);

  const exact = options.some((o) => o.toLowerCase() === value.trim().toLowerCase());
  const pick = (v) => { onChange(v); setAdding(false); setQuery(""); setOpen(false); };

  return (
    <div ref={box} className="relative">
      <Label className="text-[10px] uppercase text-gray-500">
        {label}
        {required && <span className="text-red-600 ml-0.5">*</span>}
        {optional && !required && <span className="normal-case text-gray-400"> · optional</span>}
      </Label>
      {adding ? (
        <Input
          autoFocus={!typeFirst} value={value} placeholder={placeholder || `New ${label.toLowerCase()}`}
          data-testid={tid}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") setAdding(false); }}
          className="h-8 text-xs border-blue-400"
        />
      ) : (
        <button
          type="button" onClick={() => setOpen(!open)}
          data-testid={tid}
          className="h-8 w-full flex items-center gap-1 px-2 text-xs text-left border border-gray-300 rounded bg-white hover:border-gray-400"
        >
          <span className={`truncate flex-1 ${value ? "" : "text-gray-400"}`}>
            {value || placeholder || "— any —"}
          </span>
          {value && !exact && (
            <span className="text-[9px] uppercase font-semibold text-blue-700 bg-blue-50 px-1 rounded">new</span>
          )}
          <ChevronDown className="w-3 h-3 text-gray-400" />
        </button>
      )}
      {adding && (
        <button type="button" onClick={() => { setAdding(false); setOpen(true); }}
                data-testid={`${tid}-back`}
                className="mt-1 text-[10px] text-blue-700 underline">
          pick an existing value instead
        </button>
      )}
      {open && !adding && (
        <div className="absolute z-50 mt-1 w-56 max-h-64 overflow-y-auto bg-white border border-gray-200 rounded shadow-lg p-1"
             data-testid={`${tid}-menu`}>
          <Input
            autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search..." className="h-7 text-xs mb-1"
            data-testid={`${tid}-search`}
          />
          {allowNew && (
            <button type="button" onClick={() => { onChange(query.trim()); setAdding(true); setOpen(false); }}
                    data-testid={`${tid}-add-new`}
                    className="w-full flex items-center gap-1 px-2 py-1 text-xs text-blue-700 hover:bg-blue-50 rounded font-medium">
              <Plus className="w-3 h-3" /> Add New{query.trim() ? ` “${query.trim()}”` : ""}
            </button>
          )}
          {value && (
            <button type="button" onClick={() => pick("")}
                    data-testid={`${tid}-clear`}
                    className="w-full text-left px-2 py-1 text-xs text-gray-500 hover:bg-gray-50 rounded">
              Clear
            </button>
          )}
          {groups.map(([head, list]) => (
            <div key={head}>
              <div className="px-2 pt-1.5 pb-0.5 text-[9px] uppercase tracking-wide text-gray-400">{head}</div>
              {list.map((o) => (
                <button key={o} type="button" onClick={() => pick(o)}
                        data-testid={`${tid}-option-${o}`}
                        className="w-full flex items-center gap-1 px-2 py-1 text-xs text-left hover:bg-gray-100 rounded">
                  <Check className={`w-3 h-3 ${o === value ? "text-emerald-600" : "opacity-0"}`} />
                  <span className="truncate">{o}</span>
                </button>
              ))}
            </div>
          ))}
          {groups.length === 0 && (
            <div className="px-2 py-1.5 text-[11px] text-gray-400" data-testid={`${tid}-empty`}>
              Nothing matches — use Add New.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RoomFieldCombo;
